/**
 * NameThatGuyGame.jsx
 *
 * "Name That Guy" daily: one question, four choices, 10s timer.
 * Show a feat/stat, pick which athlete did it. Silhouette reveals the real
 * photo on answer. Guest-playable. Phase 3 = play + reveal + results only
 * (no streak save / used_on stamp / leaderboard — those are Phase 4).
 */

import React, { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import Timer from './Timer';
import MultipleChoice from './MultipleChoice';
import PlayerPhoto from './PlayerPhoto';
import GameTopBar from './GameTopBar';
import ScoreBoard from './ScoreBoard';
import ResultsScreen from './ResultsScreen';
import { saveNTGDailyResult, stampNTGQuestionUsed } from '../utils/dailyChallenge';

const NTG_TIMER = 10;
const REVEAL_DELAY = 1800;

// One shuffle per question so option order stays stable across re-renders.
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const NameThatGuyGame = ({ question, currentUser, onGoHome, onShowAuth, onDailyComplete, onPlayDaily, claimedStreaks = {}, started: startedProp, onBegin }) => {
  const [startedInternal, setStartedInternal] = useState(false);
  // App-controlled (deep-link BEGIN splash screen) when onBegin is passed; otherwise self-managed.
  const started = onBegin ? startedProp : startedInternal;
  const [locked, setLocked] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [gameComplete, setGameComplete] = useState(false);
  const [passed, setPassed] = useState(false);
  const [ntgResult, setNtgResult] = useState(null);

  const lockedRef = useRef(false);
  const revealTimeoutRef = useRef(null);

  const choices = useMemo(
    () => shuffle([question.answer_name, question.wrong_1, question.wrong_2, question.wrong_3]),
    [question.id]
  );

  useEffect(() => {
    return () => { if (revealTimeoutRef.current) clearTimeout(revealTimeoutRef.current); };
  }, []);

  const finish = useCallback((didPass) => {
    setRevealed(true);
    setPassed(didPass);
    stampNTGQuestionUsed(question.id);
    revealTimeoutRef.current = setTimeout(() => {
      (async () => {
        const result = await saveNTGDailyResult(didPass, currentUser);
        setNtgResult(result);
        if (result && onDailyComplete) onDailyComplete('ntg', result.streak, result.ironmanStreak, result.strikes);
        setGameComplete(true);
      })();
    }, REVEAL_DELAY);
  }, [question.id, currentUser, onDailyComplete]);

  const handleSelect = useCallback((choice) => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    const correct = choice === question.answer_name;
    setLocked(true);
    setSelectedAnswer(choice);
    setFeedback(correct ? 'correct' : 'incorrect');
    finish(correct);
  }, [question.answer_name, finish]);

  const handleTimerExpire = useCallback(() => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    setLocked(true);
    setSelectedAnswer(null);
    setFeedback('incorrect');
    finish(false);
  }, [finish]);

  if (gameComplete) {
    return (
      <ResultsScreen
        isDailyChallenge
        isNTGDaily
        dailyResult={{ passed, streak: ntgResult?.streak ?? 0, strikes: ntgResult?.strikes ?? 0 }}
        claimedStreak={claimedStreaks?.ntg}
        currentUser={currentUser}
        myScore={passed ? 1 : 0}
        totalQuestions={1}
        myAnswers={[]}
        onGoHome={onGoHome}
        onShowAuth={onShowAuth}
        onPlayDaily={onPlayDaily}
      />
    );
  }

  if (!started) {
    return (
      <div className="round-break">
        <button className="btn-home-corner" onClick={onGoHome}>HOME</button>
        <div className="round-break-content">
          <div className="round-break-icon">👶</div>
          <div className="round-break-badge">DAILY</div>
          <h2 className="round-break-title">NAME THAT GUY JR.</h2>
          <p className="round-break-desc">{'1 question, 10 seconds — identify the "guy" to keep your streak'}</p>
          <button className="btn btn-begin" onClick={() => (onBegin ? onBegin() : setStartedInternal(true))}>BEGIN</button>
          <div className="round-break-brand">knowyourguy.com</div>
        </div>
      </div>
    );
  }

  // Withhold the photo until the reveal. NOTE: we deliberately do NOT reuse
  // Sr.'s blackout trick here. Sr. silhouettes ESPN combiner headshots, which
  // have transparent backgrounds, so brightness(0) leaves a person-shaped
  // cutout. Jr.'s photos are full-bleed images from the ntg-photos bucket, so
  // the same filter renders a solid black rectangle. The shaped placeholder in
  // PlayerPhoto carries the look instead.
  const revealPlayer = revealed
    ? { player: question.answer_name, espn_id: question.photo_url, espn_type: 'custom' }
    : { player: '', espn_id: '', espn_type: 'custom' };

  return (
    <div className="game-board">
      <GameTopBar onGoHome={onGoHome} />
      <ScoreBoard
        title={<>NAME THAT GUY <span style={{ color: '#ff3b30', fontWeight: 900 }}>JR.</span></>}
        rightLabel="1 Question"
      />

      <div className={`question-card ${feedback ? `feedback-${feedback}` : ''}`}>
        <Timer
          duration={NTG_TIMER}
          onExpire={handleTimerExpire}
          isPaused={locked}
          questionKey={question.id}
        />

        <div className="player-name-container">
          <h2 className="player-name ntg-question">{question.question}</h2>
          {question.question_note && (
            <p className="ntg-question-note">{question.question_note}</p>
          )}
          <div style={{ width: '100%', maxWidth: '200px', margin: '0 auto', height: '145px', overflow: 'hidden', background: '#111b30', borderRadius: '4px' }}>
            <PlayerPhoto player={revealPlayer} className="player-photo" />
          </div>
          {revealed && <h2 className="player-name">{question.answer_name}</h2>}
        </div>

        <MultipleChoice
          key={question.id}
          choices={choices}
          onSelect={handleSelect}
          selectedAnswer={locked ? selectedAnswer : null}
          correctAnswer={question.answer_name}
          locked={locked}
        />
      </div>
    </div>
  );
};

export default NameThatGuyGame;
